import { useEffect, useMemo, useState } from "react";
import { getRouteApi, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { AthleteHeader } from "@/components/athlete/AthleteHeader";
import { AthleteVisualizationSelect } from "@/components/athlete/AthleteVisualizationSelect";
import {
  fmtDate,
  fmtNum,
  fmtRank,
} from "@/components/athlete/formatters";
import { RaceResultsList } from "@/components/athlete/RaceResultsList";
import { RankingTrendPanel } from "@/components/athlete/RankingTrendPanel";
import { SplitProgressionPanel } from "@/components/athlete/SplitProgressionPanel";
import { AthleteEventRadarPanel } from "@/components/athlete/AthleteEventRadarPanel";
import { DataPanel, DataPanelTitle } from "@/components/ui/data-panel";
import {
  FeatureShell,
  FeatureShellHeader,
  FeatureShellCopy,
  FeatureShellEyebrow,
  FeatureShellTitle,
  FeatureShellDescription,
  FeatureShellMetrics,
  FeatureShellMetric,
  FeatureShellContent,
  FeatureCard,
  FeatureCardHeader,
} from "@/components/ui/feature-shell";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PaginationControls } from "@/components/ui/pagination-controls";
import type { TrendPoint } from "@/components/athlete/types";
import { api } from "@/lib/api";
import { useUserProfile } from "@/hooks/useUserProfile";
import "./athletes.$athleteId.css";

const routeApi = getRouteApi("/athletes/$athleteId");
const YOY_PAGE_SIZE = 10;

type ProgressionSeriesOption = {
  id: string;
  displayName: string;
  raceCount: number;
  firstYear: number | null;
  lastYear: number | null;
  latestEventDate: string | null;
};

function formatYearWindow(
  firstYear: number | null | undefined,
  lastYear: number | null | undefined,
) {
  if (firstYear == null && lastYear == null) return null;
  const start = firstYear ?? lastYear;
  const end = lastYear ?? firstYear;
  if (start == null || end == null) return null;
  return start === end ? String(start) : `${start}-${end}`;
}

export default function AthletePage() {
  const { athleteId } = routeApi.useParams();
  const { tab } = routeApi.useSearch();
  const navigate = useNavigate();
  const activeTab = tab ?? "overview";

  const [selectedSeriesId, setSelectedSeriesId] = useState<string | undefined>(
    undefined,
  );
  const [selectedRadarResultId, setSelectedRadarResultId] = useState<
    string | undefined
  >(undefined);
  const [yoyPage, setYoyPage] = useState(1);

  const { data: userProfile } = useUserProfile();

  const { data, isLoading, error } = useQuery({
    queryKey: ["athlete", athleteId],
    queryFn: async () => {
      const { data, error } = await api.GET("/athletes/{id}", {
        params: { path: { id: athleteId } },
      });
      if (error) throw error;
      return data;
    },
  });

  const {
    data: stats,
    isLoading: isStatsLoading,
    error: statsError,
  } = useQuery({
    queryKey: ["athlete-stats", athleteId],
    queryFn: async () => {
      const { data, error } = await api.GET("/athletes/{id}/stats", {
        params: {
          path: { id: athleteId },
          query: {},
        },
      });
      if (error) throw error;
      return data;
    },
  });

  const trendSeriesOptions = useMemo(() => {
    const trend = stats?.ageGroupPlaceTrend ?? [];
    const bySeries = new Map<string, ProgressionSeriesOption>();

    for (const point of trend) {
      if (!point.eventSeriesId || !point.eventSeriesName) continue;

      const year = new Date(`${point.eventDate}T00:00:00Z`).getUTCFullYear();
      const existing = bySeries.get(point.eventSeriesId);

      if (existing) {
        existing.raceCount += 1;
        existing.firstYear = Math.min(existing.firstYear ?? year, year);
        existing.lastYear = Math.max(existing.lastYear ?? year, year);
        if (
          existing.latestEventDate == null ||
          point.eventDate.localeCompare(existing.latestEventDate) > 0
        ) {
          existing.latestEventDate = point.eventDate;
        }
        continue;
      }

      bySeries.set(point.eventSeriesId, {
        id: point.eventSeriesId,
        displayName: point.eventSeriesName,
        raceCount: 1,
        firstYear: year,
        lastYear: year,
        latestEventDate: point.eventDate,
      });
    }

    return [...bySeries.values()].sort((a, b) => {
      if (a.latestEventDate && b.latestEventDate) {
        return b.latestEventDate.localeCompare(a.latestEventDate);
      }
      if (a.latestEventDate) return -1;
      if (b.latestEventDate) return 1;
      return a.displayName.localeCompare(b.displayName);
    });
  }, [stats?.ageGroupPlaceTrend]);

  const requestedSeriesId = selectedSeriesId ?? trendSeriesOptions[0]?.id;

  const {
    data: splitProgression,
    isLoading: isSplitProgressionLoading,
    error: splitProgressionError,
  } = useQuery({
    queryKey: [
      "athlete-split-progression",
      athleteId,
      requestedSeriesId ?? null,
    ],
    queryFn: async () => {
      const { data, error } = await api.GET(
        "/athletes/{id}/split-progression",
        {
          params: {
            path: { id: athleteId },
            query: requestedSeriesId ? { seriesId: requestedSeriesId } : {},
          },
        },
      );
      if (error) throw error;
      return data;
    },
  });

  const {
    data: eventRadar,
    isLoading: isEventRadarLoading,
    error: eventRadarError,
  } = useQuery({
    queryKey: ["athlete-event-radar", athleteId, selectedRadarResultId ?? null],
    queryFn: async () => {
      const { data, error } = await api.GET("/athletes/{id}/event-radar", {
        params: {
          path: { id: athleteId },
          query: selectedRadarResultId
            ? { resultId: selectedRadarResultId }
            : {},
        },
      });
      if (error) throw error;
      return data;
    },
  });

  useEffect(() => {
    setSelectedSeriesId(undefined);
    setSelectedRadarResultId(undefined);
    setYoyPage(1);
  }, [athleteId]);

  useEffect(() => {
    if (!selectedRadarResultId && eventRadar?.context.selectedResult.resultId) {
      setSelectedRadarResultId(eventRadar.context.selectedResult.resultId);
    }
  }, [selectedRadarResultId, eventRadar?.context.selectedResult.resultId]);

  const progressionSeriesOptions = useMemo(() => {
    if (!splitProgression?.availableSeries.length) return trendSeriesOptions;

    const trendSeriesById = new Map(
      trendSeriesOptions.map((series) => [series.id, series]),
    );

    return splitProgression.availableSeries.map((series) => {
      const fallback = trendSeriesById.get(series.id);
      return {
        id: series.id,
        displayName: series.displayName,
        raceCount: series.raceCount,
        firstYear: series.firstYear ?? fallback?.firstYear ?? null,
        lastYear: series.lastYear ?? fallback?.lastYear ?? null,
        latestEventDate: fallback?.latestEventDate ?? null,
      } satisfies ProgressionSeriesOption;
    });
  }, [splitProgression?.availableSeries, trendSeriesOptions]);

  const activeSeriesId =
    requestedSeriesId ??
    splitProgression?.selectedSeriesId ??
    progressionSeriesOptions[0]?.id;
  const selectedProgressionSeries = progressionSeriesOptions.find(
    (series) => series.id === activeSeriesId,
  );
  const selectedSeriesWindow = formatYearWindow(
    selectedProgressionSeries?.firstYear,
    selectedProgressionSeries?.lastYear,
  );

  const filteredTrend = useMemo(() => {
    const trend = (stats?.ageGroupPlaceTrend ?? []) as TrendPoint[];
    if (!activeSeriesId) return trend;
    return trend.filter((point) => point.eventSeriesId === activeSeriesId);
  }, [activeSeriesId, stats?.ageGroupPlaceTrend]);

  const filteredYoyRows = useMemo(() => {
    const byYear = new Map<number, number[]>();
    for (const point of filteredTrend) {
      const year = new Date(`${point.eventDate}T00:00:00Z`).getFullYear();
      const ranks = byYear.get(year) ?? [];
      ranks.push(point.ageGroupRank);
      byYear.set(year, ranks);
    }
    const years = [...byYear.keys()].sort((a, b) => a - b);
    const result: { year: number; avgAgeGroupRank: number; racesInYear: number; deltaFromPriorYear: number | null }[] = [];
    for (let i = 0; i < years.length; i++) {
      const year = years[i]!;
      const ranks = byYear.get(year)!;
      const avg = Math.round((ranks.reduce((s, r) => s + r, 0) / ranks.length) * 10) / 10;
      const prior = i > 0 ? result[i - 1]! : null;
      result.push({
        year,
        avgAgeGroupRank: avg,
        racesInYear: ranks.length,
        deltaFromPriorYear: prior != null ? Math.round((avg - prior.avgAgeGroupRank) * 10) / 10 : null,
      });
    }
    return result;
  }, [filteredTrend]);

  useEffect(() => {
    if (data?.mergedIntoId) {
      navigate({
        to: "/athletes/$athleteId",
        params: { athleteId: data.mergedIntoId },
        replace: true,
      });
    }
  }, [data?.mergedIntoId, navigate]);

  if (isLoading) return <p className="text-muted-foreground">Loading...</p>;
  if (error) return <p className="text-destructive">Error loading athlete</p>;
  if (!data) return null;
  if (data.mergedIntoId) return null;

  const { athlete, results } = data;
  const sortedResults = [...results].sort((a, b) => {
    if (a.eventDate && b.eventDate)
      return b.eventDate.localeCompare(a.eventDate);
    if (a.eventDate) return -1;
    if (b.eventDate) return 1;
    return a.eventName.localeCompare(b.eventName);
  });

  const reversedYoyRows = [...filteredYoyRows].reverse();
  const totalYoyPages = Math.max(
    Math.ceil(reversedYoyRows.length / YOY_PAGE_SIZE),
    1,
  );
  const clampedYoyPage = Math.min(yoyPage, totalYoyPages);
  const yoyStartIndex = (clampedYoyPage - 1) * YOY_PAGE_SIZE;
  const paginatedYoyRows = reversedYoyRows.slice(
    yoyStartIndex,
    yoyStartIndex + YOY_PAGE_SIZE,
  );

  // Derive last raced age group from most recent result
  const lastAgeGroup = sortedResults.find((r) => r.ageGroup)?.ageGroup ?? null;

  const isClaimed = !!athlete.profileId;
  const isOwnProfile =
    isClaimed &&
    userProfile?.claimedAthletes?.some((a) => a.id === athlete.id) === true;

  return (
    <div className="athlete-page">
      <AthleteHeader
        athlete={athlete}
        lastAgeGroup={lastAgeGroup}
        isClaimed={isClaimed}
        isOwnProfile={isOwnProfile}
      />

      {/* Persistent hero metrics */}
      {isStatsLoading && (
        <DataPanel className="text-sm text-muted-foreground athlete-status-message">
          Computing stats...
        </DataPanel>
      )}

      {statsError && (
        <DataPanel className="text-sm text-destructive athlete-status-message">
          Stats unavailable right now. Race history is still available below.
        </DataPanel>
      )}

      {stats && (
        <div className="athlete-hero-metrics">
          <FeatureShellMetric label="Races" value={fmtNum(stats.summary.totalRaces)} />
          <FeatureShellMetric label="Best Overall" value={fmtRank(stats.performance.bestOverallRank)} />
          <FeatureShellMetric label="Top 10 Finishes" value={fmtNum(stats.performance.top10Count)} />
          <FeatureShellMetric label="Avg Overall" value={fmtNum(stats.performance.avgOverallRank != null ? Math.round(stats.performance.avgOverallRank) : null)} />
          <FeatureShellMetric label="Total Points" value={fmtNum(stats.summary.totalPoints)} />
          <FeatureShellMetric label="Best AG" value={fmtRank(stats.competitive.bestAgeGroupRank)} />
        </div>
      )}

      {/* Tabbed content */}
      <Tabs
        value={activeTab}
        onValueChange={(value) => {
          navigate({
            search: (prev: Record<string, unknown>) => ({
              ...prev,
              tab: value === "overview" ? undefined : value,
            }),
            replace: true,
          });
        }}
      >
        <TabsList className="athlete-tabs-list">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="progression">Progression</TabsTrigger>
          <TabsTrigger value="results">Results</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          {stats && (
            <div className="athlete-overview-cards">
              {(["overall", "gender", "ageGroup"] as const).some((dim) => {
                const lb = stats.competitive.leaderboard[dim];
                return (
                  lb.top1Count > 0 || lb.top3Count > 0 || lb.top10Count > 0
                );
              }) && (
              <FeatureCard className="athlete-card-competitive-snapshot">
                <FeatureCardHeader>
                  <DataPanelTitle>Competitive Snapshot</DataPanelTitle>
                  <p className="feature-card-copy">
                    Based on completed races with available rank data.
                  </p>
                </FeatureCardHeader>
                <div className="athlete-competitive-snapshot-grid">
                  {(["overall", "gender", "ageGroup"] as const).map((dim) => {
                    const lb = stats.competitive.leaderboard[dim];
                    if (
                      lb.top1Count === 0 &&
                      lb.top3Count === 0 &&
                      lb.top10Count === 0
                    ) {
                      return null;
                    }
                    const label =
                      dim === "ageGroup"
                        ? "Age Group"
                        : dim.charAt(0).toUpperCase() + dim.slice(1);
                    const accentClass = `athlete-competitive-snapshot-card--${dim}`;
                    return (
                      <div
                        key={dim}
                        className={`athlete-competitive-snapshot-card ${accentClass}`}
                      >
                        <div className="athlete-competitive-snapshot-card-head">
                          <p className="athlete-competitive-snapshot-dim-label">
                            {label}
                          </p>
                          <span className="athlete-competitive-snapshot-sample">
                            {lb.sampleSize} races
                          </span>
                        </div>
                        <div className="athlete-competitive-snapshot-stats">
                          <div className="athlete-competitive-snapshot-stat-row">
                            <span className="athlete-competitive-snapshot-stat-label">
                              1st
                            </span>
                            <div className="athlete-competitive-snapshot-stat-values">
                              <span className="athlete-competitive-snapshot-stat-count">
                                {lb.top1Count}
                              </span>
                              <span className="athlete-competitive-snapshot-stat-rate">
                                {lb.top1RatePct}%
                              </span>
                            </div>
                          </div>
                          <div className="athlete-competitive-snapshot-stat-row">
                            <span className="athlete-competitive-snapshot-stat-label">
                              Top 3
                            </span>
                            <div className="athlete-competitive-snapshot-stat-values">
                              <span className="athlete-competitive-snapshot-stat-count">
                                {lb.top3Count}
                              </span>
                              <span className="athlete-competitive-snapshot-stat-rate">
                                {lb.top3RatePct}%
                              </span>
                            </div>
                          </div>
                          <div className="athlete-competitive-snapshot-stat-row">
                            <span className="athlete-competitive-snapshot-stat-label">
                              Top 10
                            </span>
                            <div className="athlete-competitive-snapshot-stat-values">
                              <span className="athlete-competitive-snapshot-stat-count">
                                {lb.top10Count}
                              </span>
                              <span className="athlete-competitive-snapshot-stat-rate">
                                {lb.top10RatePct}%
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </FeatureCard>
              )}

              {(stats.percentile.overall.sampleSize > 0 ||
                stats.percentile.ageGroup.sampleSize > 0) && (
                <FeatureCard className="athlete-card-percentile-finish">
                  <FeatureCardHeader>
                    <DataPanelTitle>Percentile Finish</DataPanelTitle>
                    <p className="feature-card-copy">
                      How you rank relative to the full field in each race.
                    </p>
                  </FeatureCardHeader>
                  <div className="athlete-percentile-grid">
                    {(["overall", "ageGroup"] as const).map((dim) => {
                      const pct = stats.percentile[dim];
                      if (pct.sampleSize === 0) return null;
                      const label =
                        dim === "ageGroup" ? "Age Group" : "Overall";
                      return (
                        <div key={dim} className="athlete-percentile-card">
                          <p className="athlete-leaderboard-dim-label">
                            {label}
                          </p>
                          <div className="athlete-percentile-hero">
                            <span className="athlete-percentile-value">
                              {pct.best != null
                                ? `${pct.best.toFixed(0)}%`
                                : "—"}
                            </span>
                            <span className="athlete-percentile-caption">
                              best percentile
                            </span>
                          </div>
                          <div className="athlete-leaderboard-stats">
                            <div className="athlete-leaderboard-stat-row">
                              <span className="athlete-leaderboard-stat-label">
                                Avg Percentile
                              </span>
                              <span className="athlete-leaderboard-stat-value">
                                {pct.avg != null
                                  ? `${pct.avg.toFixed(1)}%`
                                  : "—"}
                              </span>
                            </div>
                            <div className="athlete-leaderboard-stat-row">
                              <span className="athlete-leaderboard-stat-label">
                                Median
                              </span>
                              <span className="athlete-leaderboard-stat-value">
                                {pct.median != null
                                  ? `${pct.median.toFixed(1)}%`
                                  : "—"}
                              </span>
                            </div>
                            <div className="athlete-leaderboard-stat-row">
                              <span className="athlete-leaderboard-stat-label">
                                Races
                              </span>
                              <span className="athlete-leaderboard-stat-value">
                                {pct.sampleSize}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </FeatureCard>
              )}

              {stats.personalBests.length > 0 && (
                <FeatureCard className="athlete-card-personal-bests">
                  <FeatureCardHeader>
                    <DataPanelTitle>Personal Bests</DataPanelTitle>
                  </FeatureCardHeader>
                  <div className="athlete-pb-groups">
                    {(() => {
                      const grouped = new Map<string, typeof stats.personalBests>();
                      for (const pb of stats.personalBests) {
                        const list = grouped.get(pb.raceType) ?? [];
                        list.push(pb);
                        grouped.set(pb.raceType, list);
                      }
                      return [...grouped.entries()].map(([raceType, pbs]) => (
                        <div key={raceType} className="athlete-pb-group">
                          <p className="athlete-pb-race-type">
                            {raceType.replace(/_/g, " ")}
                          </p>
                          <div className="athlete-pb-list">
                            {pbs.map((pb) => (
                              <Link
                                key={pb.resultId}
                                to="/results/$resultId"
                                params={{ resultId: pb.resultId }}
                                className={`athlete-pb-row ${pb.rank === 1 ? "athlete-pb-row--best" : ""}`}
                              >
                                <span className="athlete-pb-rank">#{pb.rank}</span>
                                <div className="athlete-pb-detail">
                                  <span className="athlete-pb-time">{pb.finishTimeFormatted}</span>
                                  <span className="athlete-pb-event">
                                    {pb.eventName}
                                    {pb.eventDate ? ` · ${fmtDate(pb.eventDate)}` : ""}
                                  </span>
                                </div>
                              </Link>
                            ))}
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </FeatureCard>
              )}

              {stats.splitRankHighlights.length > 0 && (
                <FeatureCard className="athlete-card-best-split-rankings">
                  <FeatureCardHeader>
                    <DataPanelTitle>Best Split Rankings</DataPanelTitle>
                  </FeatureCardHeader>
                  <div className="athlete-split-grid">
                    {stats.splitRankHighlights.map((split) =>
                      split.resultId ? (
                        <Link
                          key={split.segmentType}
                          to="/results/$resultId"
                          params={{ resultId: split.resultId }}
                          className="athlete-split-card"
                        >
                          <p className="athlete-split-type">{split.segmentType}</p>
                          <p className="athlete-split-rank">
                            {fmtRank(split.bestAgeGroupRank)}
                          </p>
                          <p className="athlete-split-event">
                            {split.eventName
                              ? `${split.eventName}${split.eventDate ? ` · ${fmtDate(split.eventDate)}` : ""}`
                              : "No data"}
                          </p>
                        </Link>
                      ) : (
                        <div key={split.segmentType} className="athlete-split-card">
                          <p className="athlete-split-type">{split.segmentType}</p>
                          <p className="athlete-split-rank">
                            {fmtRank(split.bestAgeGroupRank)}
                          </p>
                          <p className="athlete-split-event">No data</p>
                        </div>
                      )
                    )}
                  </div>
                </FeatureCard>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="progression">
          {stats && (
            <div className="athlete-progression-content">
              <FeatureShell className="athlete-progression-shell">
                <FeatureShellHeader>
                  <FeatureShellCopy>
                    <FeatureShellEyebrow>Series Lens</FeatureShellEyebrow>
                    <FeatureShellTitle>Performance progression</FeatureShellTitle>
                    <FeatureShellDescription>
                      One event-series view for rank movement, seasonal averages,
                      and split speed.
                    </FeatureShellDescription>
                  </FeatureShellCopy>
                  {progressionSeriesOptions.length > 0 ? (
                    <AthleteVisualizationSelect
                      label="Event series"
                      value={
                        activeSeriesId ?? progressionSeriesOptions[0]?.id ?? ""
                      }
                      placeholder="Select series"
                      options={progressionSeriesOptions.map((series) => ({
                        value: series.id,
                        label: series.displayName,
                      }))}
                      onValueChange={(value) => {
                        setSelectedSeriesId(value);
                        setYoyPage(1);
                      }}
                      wrapperClassName="athlete-progression-select"
                      triggerClassName="athlete-progression-select-trigger"
                    />
                  ) : null}
                </FeatureShellHeader>

                {selectedProgressionSeries ? (
                  <FeatureShellMetrics>
                    <FeatureShellMetric
                      label="Focus"
                      value={selectedProgressionSeries.displayName}
                    />
                    <FeatureShellMetric
                      label="Window"
                      value={selectedSeriesWindow ?? "Multiple seasons"}
                    />
                    <FeatureShellMetric
                      label="Races tracked"
                      value={fmtNum(selectedProgressionSeries.raceCount)}
                    />
                  </FeatureShellMetrics>
                ) : null}

                <FeatureShellContent>
                  <FeatureCard className="athlete-progression-card--trend">
                    <RankingTrendPanel trend={filteredTrend} />
                  </FeatureCard>

                  {filteredYoyRows.length >= 2 ? (
                    <FeatureCard>
                      <FeatureCardHeader>
                        <DataPanelTitle>
                          Year-over-Year Progression
                        </DataPanelTitle>
                        <p className="feature-card-copy">
                          Age-group rank by racing year.
                        </p>
                      </FeatureCardHeader>
                      <div className="athlete-yoy-table-wrap">
                        <table className="athlete-yoy-table">
                          <thead>
                            <tr>
                              <th>Year</th>
                              <th>AG Rank</th>
                              <th>Change</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedYoyRows.map((row) => (
                              <tr key={row.year}>
                                <td className="athlete-yoy-year">{row.year}</td>
                                <td>#{row.avgAgeGroupRank}</td>
                                <td>
                                  {row.deltaFromPriorYear != null ? (
                                    <span
                                      className={
                                        row.deltaFromPriorYear < 0
                                          ? "athlete-yoy-improved"
                                          : row.deltaFromPriorYear > 0
                                            ? "athlete-yoy-regressed"
                                            : "athlete-yoy-flat"
                                      }
                                    >
                                      {row.deltaFromPriorYear < 0
                                        ? `↑ ${Math.abs(row.deltaFromPriorYear)}`
                                        : row.deltaFromPriorYear > 0
                                          ? `↓ ${row.deltaFromPriorYear}`
                                          : "—"}
                                    </span>
                                  ) : (
                                    "—"
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <PaginationControls
                        page={clampedYoyPage}
                        pageSize={YOY_PAGE_SIZE}
                        totalCount={reversedYoyRows.length}
                        currentCount={paginatedYoyRows.length}
                        onPageChange={(nextPage) =>
                          setYoyPage(
                            Math.max(1, Math.min(nextPage, totalYoyPages)),
                          )
                        }
                        itemLabel="years"
                        className="athlete-progression-pagination"
                      />
                    </FeatureCard>
                  ) : null}

                  <FeatureCard>
                    <SplitProgressionPanel
                      progression={splitProgression}
                      isLoading={isSplitProgressionLoading}
                      error={splitProgressionError}
                      selectedSeriesId={activeSeriesId ?? undefined}
                      onSeriesChange={setSelectedSeriesId}
                      showSeriesSelect={false}
                    />
                  </FeatureCard>
                </FeatureShellContent>
              </FeatureShell>

              <FeatureCard className="athlete-panel-visualization athlete-race-split-card">
                <AthleteEventRadarPanel
                  radar={eventRadar}
                  isLoading={isEventRadarLoading}
                  error={eventRadarError}
                  selectedResultId={selectedRadarResultId}
                  onResultChange={setSelectedRadarResultId}
                />
              </FeatureCard>
            </div>
          )}
        </TabsContent>

        <TabsContent value="results">
          <RaceResultsList results={sortedResults} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
